import React from 'react';
import { FormLabel } from '../ShelfPage/styles';
import UrlListTable from './UrlListTable';
import axios from 'axios';
import { buildLlmCrawlerPath } from 'src/utils/app';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import _get from 'lodash/get';
import { fetchRetailerNameMap } from '../utils/retailers';
import { TemplateInputHeader, RadioRow, RadioCard, SectionSubtext, FieldLabel } from '../shared';
import RetailersSummary from './RetailersSummary';
import StyledInput from 'src/components/BeaconV2Components/common/StyledInput';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';

export interface SummaryCampaignConfigurationProps {
  template: string;
  editableName: string;
  setEditableName: (v: string) => void;
  createdBy: string | null;
  createdTime?: string | number | null;
  endTime?: string | number | null;
  isSaving: boolean;
  saveStatus: 'idle' | 'success' | 'error';
  onSave: () => void;
  campaignId?: string | number | null;
  canSave?: boolean;
  status?: 'enabled' | 'disabled';
  isUpdatingStatus?: boolean;
  onChangeStatus?: (next: 'enabled' | 'disabled') => void;
}

const SummaryCampaignConfiguration: React.FC<SummaryCampaignConfigurationProps> = ({
  template,
  editableName,
  setEditableName,
  createdBy,
  createdTime,
  endTime,
  isSaving,
  saveStatus,
  onSave,
  campaignId,
  canSave = true,
  status = 'enabled',
  isUpdatingStatus = false,
  onChangeStatus,
}) => {
  const [contextUrls, setContextUrls] = React.useState<string[]>([]);
  const [retailerIds, setRetailerIds] = React.useState<string[]>([]);
  const [retailerNameById, setRetailerNameById] = React.useState<Record<string, string>>({});
  const divisionId = useAppSelector((state) => _get(state, ['user', 'session', 'divisionId'], null));

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      if (!campaignId) return;
      try {
        const path = buildLlmCrawlerPath(`crawl-campaigns/${campaignId}/context`);
        const { data } = await axios.get(path, { params: { env: 'dev', divisionId } });
        let urls: string[] = [];
        let retailers: string[] = [];
        if (data?.context && typeof data.context === 'object') {
          // Expected format: { retailerId1: [url1, url2], retailerId2: [...] }
          const ctx = data.context as Record<string, unknown>;
          retailers = Object.keys(ctx).filter((k) => {
            const v = (ctx as any)[k];
            return Array.isArray(v) && v.length > 0;
          });
          urls = Object.values(ctx).reduce<string[]>((acc, val) => {
            if (Array.isArray(val)) {
              const cleaned = (val as any[]).filter((s) => typeof s === 'string') as string[];
              acc.push(...cleaned);
            }
            return acc;
          }, []);
        } else if (Array.isArray(data?.urls)) {
          urls = data.urls as string[];
        } else if (Array.isArray(data)) {
          urls = data as string[];
        }
        if (mounted) {
          setContextUrls(urls);
          setRetailerIds(retailers);
        }
      } catch (e) {
        if (mounted) {
          setContextUrls([]);
          setRetailerIds([]);
        }
      }
    })();
    return () => { mounted = false; };
  }, [campaignId, divisionId]);

  // Fetch retailer metadata to map id -> name
  React.useEffect(() => {
    let active = true;
    (async () => {
      const map = await fetchRetailerNameMap();
      if (active) setRetailerNameById(map);
    })();
    return () => { active = false; };
  }, []);
  return (
    <div style={{ width: '1040px', display: 'grid', gridTemplateColumns: '1fr', justifyItems: 'stretch', rowGap: 24, paddingBottom: 32 }}>
      <div style={{ display: 'grid', rowGap: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 20, marginTop: 18 }}>Summary</div>
        <div style={{ width: '100%', height: 1, background: '#E6ECF2' }} />
      </div>

      <div style={{ width: '100%' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', justifyItems: 'stretch', rowGap: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '180px minmax(0, 1fr)', alignItems: 'center', width: '100%' }}>
            <FormLabel>Agent Name:</FormLabel>
            <StyledInput
              value={editableName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditableName(e.target.value)}
              placeholder="Agent name"
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '180px minmax(0, 1fr)', alignItems: 'center', width: '100%' }}>
            <FormLabel>Template:</FormLabel>
            <div>{template}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '180px minmax(0, 1fr)', alignItems: 'center', width: '100%' }}>
            <FormLabel>Created By:</FormLabel>
            <div>{createdBy || '—'}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '180px minmax(0, 1fr)', alignItems: 'center', width: '100%' }}>
            <FormLabel>Created Date:</FormLabel>
            <div>{createdTime ? new Date(createdTime).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '180px minmax(0, 1fr)', alignItems: 'center', width: '100%' }}>
            <FormLabel>End Date:</FormLabel>
            <div>{endTime ? new Date(endTime).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '180px minmax(0, 1fr)', alignItems: 'center', width: '100%' }}>
            <FormLabel>Status:</FormLabel>
            <div style={{ width: 220 }}>
              <SlDropdownMenu
                disabled={isUpdatingStatus}
                options={[
                  { id: 'enabled', label: 'Enabled' },
                  { id: 'disabled', label: 'Disabled' }
                ]}
                selectedId={status}
                onChange={(opt) => onChangeStatus && onChangeStatus((opt as any).id as 'enabled' | 'disabled')}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={{ width: '100%' }}>
        <div style={{ fontWeight: 600, fontSize: 20, marginBottom: 8 }}>Inputs</div>
        <div style={{ width: '100%', height: 1, background: '#E6ECF2', marginBottom: 12 }} />

        {/* Input Type selection (below Inputs header, above Product URLs table) */}
        <div style={{ marginTop: 0, marginBottom: 8 }}>
          <TemplateInputHeader style={{ marginBottom: 12 }}>Input Type</TemplateInputHeader>
          <RadioRow style={{ columnGap: 48 }}>
            <RadioCard selected style={{ border: 'none', background: 'transparent', padding: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="radio" name="configInputType" defaultChecked style={{ accentColor: '#031425' }} />
                <span style={{ fontWeight: 700, fontSize: 16, color: '#031425' }}>Product URLs</span>
              </div>
              <SectionSubtext style={{ marginTop: 8, marginBottom: 0, fontSize: 14, lineHeight: '20px', color: '#031425', marginLeft: 24, fontWeight: 400 }}>
                Select individual products to get pricing on. Use this option to save cost or when you know exactly the
                SKUs you need data for.
              </SectionSubtext>
            </RadioCard>

            <RadioCard style={{ border: 'none', background: 'transparent', padding: 0, opacity: 0.55, cursor: 'not-allowed' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="radio" name="configInputType" disabled style={{ accentColor: '#C9D6E2' }} />
                <span style={{ fontWeight: 700, fontSize: 16, color: '#031425' }}>Search Terms</span>
              </div>
              <SectionSubtext style={{ marginTop: 8, marginBottom: 0, fontSize: 14, lineHeight: '20px', color: '#031425', marginLeft: 24 }}>
                Select search terms that Stackline will search on the retailers chosen. Use this option when you have a
                broad catalog or aren’t sure exactly what products you need.
              </SectionSubtext>
            </RadioCard>
          </RadioRow>
        </div>

        {/* Product URLs table with explicit title */}
        <div style={{ marginTop: 8 }}>
          <FieldLabel style={{ fontSize: 16 }}>Product URLs</FieldLabel>
          {contextUrls.length > 0 ? (
            <UrlListTable urls={contextUrls} rowsPerPage={5} useValidityColors={false} />
          ) : (
            <FormLabel>No inputs found for this campaign.</FormLabel>
          )}
        </div>
      </div>

      <RetailersSummary retailerIds={retailerIds} retailerNameById={retailerNameById} />

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <div>
          {saveStatus !== 'idle' && (
            <div style={{ fontSize: 12 }}>
              {saveStatus === 'success' ? (
                <span style={{ color: '#0B6B3A' }}>Saved. Redirecting to Summary…</span>
              ) : (
                <span style={{ color: '#9B1C1C' }}>Failed to save. Redirecting…</span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onSave} disabled={isSaving || !canSave} style={{ background: '#0E1E3A', color: '#fff', padding: '8px 14px', borderRadius: 8, border: 'none', cursor: isSaving || !canSave ? 'not-allowed' : 'pointer', opacity: isSaving || !canSave ? 0.6 : 1 }}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SummaryCampaignConfiguration;


